import React, { useId, useState } from 'react';
import { Popover } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import './InfoPopover.css';

interface InfoPopoverProps {
  text: string;
  learnMoreUrl?: string;
  ariaLabel?: string;
}

/**
 * InfoPopover Component
 * Click-to-open info popover (sibling of the hover-only InfoTooltip). Used where the
 * help content needs an actionable link, since hover tooltips can't hold interactive children.
 */
const InfoPopover: React.FC<InfoPopoverProps> = ({ text, learnMoreUrl, ariaLabel = 'More info' }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const popoverId = useId();

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  return (
    <>
      <button
        type="button"
        className="info-popover-trigger"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={handleToggle}
      >
        <InfoOutlinedIcon className="info-popover-icon" sx={{ fontSize: 15 }} />
      </button>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            className: 'info-popover-paper',
            role: 'dialog',
            'aria-label': ariaLabel,
            // Style the paper via sx (emotion) so it reliably beats MUI's own
            // emotion-generated .MuiPaper-root/.MuiPopover-paper rules. An external
            // CSS class loses on source order (MUI injects later), which left the
            // paper white — making the white description text invisible.
            sx: {
              backgroundColor: '#201428',
              backgroundImage: 'none',
              border: '1px solid #323232',
              borderRadius: '8px',
              maxWidth: 240,
            },
          },
        }}
      >
        <div className="info-popover-content">
          <p className="info-popover-text">{text}</p>
          {learnMoreUrl && (
            <a
              className="info-popover-link"
              href={learnMoreUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more →
            </a>
          )}
        </div>
      </Popover>
    </>
  );
};

export default InfoPopover;
